import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, ExternalLink, Copy, ShoppingCart, Loader2, CreditCard } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { MillieAvatar } from "./MascotBubble";
import { Barcode } from "./RewardsWallet";
import FillTrolley from "./FillTrolley";

const SHOPS = [{ key: "woolies", label: "Woolies", color: "#3E8E41" }, { key: "coles", label: "Coles", color: "#E01A22" }];

export default function HandoffSheet({ open, onClose, activeId, defaultShop = "woolies", cur = "A$", onChanged }) {
  const [shop, setShop] = useState(defaultShop);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !activeId) return;
    setLoading(true); setData(null);
    api.get(`/shopping/${activeId}/handoff`, { params: { shop } }).then(({ data }) => setData(data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail))).finally(() => setLoading(false));
  }, [open, activeId, shop]);

  const copy = async () => { await navigator.clipboard?.writeText(data.text); toast.success("List copied — paste it into the search or your notes"); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
          className="fixed inset-0 z-50 bg-sorted-ink/40 backdrop-blur-sm flex items-end justify-center" data-testid="handoff-sheet">
          <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#FFF6F1] rounded-t-[2rem] p-6 max-h-[88vh] overflow-y-auto">
            <div className="flex items-center gap-3">
              <MillieAvatar size={44} float={false} mood="point" />
              <div className="flex-1">
                <p className="font-hand text-2xl text-sorted-ink leading-none">Off to the shops</p>
                <p className="text-xs text-sorted-slate font-semibold">I'll get your trolley there — you check out in their app so your points land.</p>
              </div>
              <button onClick={onClose} aria-label="Close" data-testid="handoff-close" className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-sorted-slate"><X size={16} strokeWidth={3} /></button>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-4">
              {SHOPS.map((s) => (
                <button key={s.key} onClick={() => setShop(s.key)} data-testid={`handoff-shop-${s.key}`}
                  className={`py-3 rounded-full font-bold text-sm border-2 transition-colors ${shop === s.key ? "text-white border-transparent" : "bg-white border-[#FDECEF] text-sorted-ink"}`}
                  style={shop === s.key ? { background: s.color } : {}}>{s.label}</button>
              ))}
            </div>

            {loading || !data ? (
              <div className="mt-4 flex items-center gap-2 text-sm text-sorted-slate"><Loader2 size={16} className="animate-spin" /> Sorting your list…</div>
            ) : (
              <>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <a href={data.home} target="_blank" rel="noreferrer" data-testid="handoff-open-shop"
                    className="bg-sorted-rose text-white text-sm font-bold py-3 rounded-full flex items-center justify-center gap-1.5"><ShoppingCart size={15} strokeWidth={2.5} /> Open {data.label}</a>
                  <button onClick={copy} data-testid="handoff-copy" className="bg-white border-2 border-[#FDECEF] text-sorted-ink text-sm font-bold py-3 rounded-full flex items-center justify-center gap-1.5"><Copy size={15} strokeWidth={2.5} /> Copy list</button>
                </div>

                <FillTrolley activeId={activeId} shop={shop} label={data.label} onDone={onChanged} />

                <div className="mt-4 bg-white rounded-3xl border-2 border-[#FDECEF] p-3" data-testid="handoff-rewards">
                  <p className="text-xs font-bold text-sorted-slate flex items-center gap-1"><CreditCard size={12} strokeWidth={2.5} /> {data.rewards_label}</p>
                  {data.rewards_number ? (
                    <>
                      <p className="text-xs text-sorted-slate mt-0.5">Scan this at the register, or make sure it's linked in the {data.label} app before you check out online.</p>
                      <Barcode value={data.rewards_number} testId="handoff-barcode" />
                    </>
                  ) : (
                    <p className="text-xs text-sorted-slate mt-0.5">No {data.rewards_label} card saved yet — add it under Profile → Rewards wallet so you never miss points.</p>
                  )}
                </div>

                <p className="mt-4 text-xs font-bold text-sorted-slate">{data.items.length} things to grab · est. {cur}{Number(data.estimated_total).toFixed(0)}</p>
                <div className="mt-2 space-y-1.5">
                  {data.items.map((it) => (
                    <a key={it.name} href={it.search_url} target="_blank" rel="noreferrer" data-testid={`handoff-item-${it.name}`}
                      className="flex items-center gap-2 bg-white rounded-2xl px-3 py-2.5 border-2 border-[#FDECEF]">
                      <span className="flex-1 text-sm font-semibold text-sorted-ink truncate">{it.name}</span>
                      {(it.quantity || it.unit) && <span className="text-xs font-bold text-sorted-slate">{it.quantity} {it.unit}</span>}
                      <ExternalLink size={14} strokeWidth={2.5} className="text-sorted-slate shrink-0" />
                    </a>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
